/**
 * Herkunft der Kooperation und Nachprovision nach Austritt (0074), rein und ohne Datenbank. Gleiche Reihenfolge wie der
 * Datenbank-Wächter app.freeze_deal_commission beim Anlegen einer Kooperation:
 *   1  an der Kooperation eingetragener Satz bzw. eigene Staffel
 *   2  Nachlaufregel (Creator archiviert, Regel „angebahnt“, Abschlusstag im Fenster): Satz der Staffel zum Abschlusstag
 *   3  Satz der Herkunft (Einstellungen der Agentur), falls hinterlegt
 *   4  Staffel bzw. Satz des Creators
 *   5  Standard der Agentur
 * Der ermittelte Satz wird festgeschrieben; spätere Änderungen an Standards, Herkunftssätzen oder Regeln ändern ihn nicht.
 * Ausnahme: laufende Kooperationen, die beim Austritt umgestellt wurden (deals.post_term), rechnen je Abrechnungsmonat nach
 * der Staffel ab (postTermMonthBp, dealBpForMonth), nach dem Ende mit dem Satz „danach“ (Standard 0).
 */
import type { IsoDate } from "./types.js";

export type DealOrigin = "creator" | "agency" | "brand";
export const DEAL_ORIGINS: readonly DealOrigin[] = ["creator", "agency", "brand"];

export const ORIGIN_LABEL: Record<DealOrigin, { de: string; en: string }> = {
  creator: { de: "Eigenakquise des Creators", en: "Found by the creator" },
  agency: { de: "Akquise der Agentur", en: "Found by the agency" },
  brand: { de: "Anfrage der Marke", en: "Brand inquiry" },
};

export function isDealOrigin(v: unknown): v is DealOrigin {
  return typeof v === "string" && (DEAL_ORIGINS as readonly string[]).includes(v);
}

/** Sätze je Herkunft in Basispunkten ({"creator": 1000}); fehlt ein Eintrag, gilt der Standard. */
export type OriginRates = Partial<Record<DealOrigin, number>>;

export function validOriginRates(v: unknown): v is OriginRates {
  if (v === null || v === undefined) return true;
  if (typeof v !== "object" || Array.isArray(v)) return false;
  return Object.entries(v as Record<string, unknown>).every(([k, bp]) => isDealOrigin(k) && typeof bp === "number" && Number.isInteger(bp) && bp >= 0 && bp <= 10000);
}

// ── Nachlaufregel ───────────────────────────────────────────────────────────────

export interface PostTermStep {
  /** bis einschließlich zu diesem Monat nach dem Austritt (1 = erster Monat) */
  upto: number;
  bp: number;
}
export interface PostTermRule {
  months: number;
  /** fester Satz (ohne Staffel) */
  bp?: number;
  /** zeitliche Staffel, aufsteigend; die letzte Stufe endet mit der Dauer */
  steps?: PostTermStep[];
  /** concluded: nur in der Laufzeit abgeschlossene; initiated: auch angebahnte, im Nachlauf abgeschlossene */
  scope?: "concluded" | "initiated";
  /** laufende Kooperationen wechseln auf den Nachlaufsatz (Standard ja) */
  running?: boolean;
  /** Satz umgestellter laufender Kooperationen nach dem Ende der Regel (fehlt = 0, keine Agenturprovision) */
  after_bp?: number;
}

const KEYS = new Set(["months", "bp", "steps", "scope", "running", "after_bp"]);
const intIn = (v: unknown, lo: number, hi: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;

/** Gleiche Regeln wie app.valid_post_term: 1 bis 60 Monate, Satz 0 bis 100 % oder bis zu sechs aufsteigende Stufen bis zur Dauer. */
export function validPostTermRule(v: unknown): v is PostTermRule {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const r = v as Record<string, unknown>;
  if (Object.keys(r).some((k) => !KEYS.has(k))) return false;
  if (!intIn(r.months, 1, 60)) return false;
  if (r.scope !== undefined && r.scope !== "concluded" && r.scope !== "initiated") return false;
  if (r.running !== undefined && typeof r.running !== "boolean") return false;
  if (r.after_bp !== undefined && !intIn(r.after_bp, 0, 10000)) return false;
  if (r.steps !== undefined) {
    if (r.bp !== undefined || !Array.isArray(r.steps) || r.steps.length < 1 || r.steps.length > 6) return false;
    let prev = 0;
    for (const s of r.steps as unknown[]) {
      if (!s || typeof s !== "object") return false;
      const st = s as Record<string, unknown>;
      if (!intIn(st.upto, 1, 60) || !intIn(st.bp, 0, 10000) || st.upto <= prev) return false;
      prev = st.upto;
    }
    return prev === r.months;
  }
  return intIn(r.bp, 0, 10000);
}

/** Volle Monate zwischen zwei Tagen (wie age() in PostgreSQL): 31.01. → 28.02. ist 0, → 01.03. ist 1 (Monatsende zählt als erreicht). */
export function fullMonthsBetween(from: IsoDate, on: IsoDate): number {
  const [fy, fm, fd] = from.split("-").map(Number) as [number, number, number];
  const [oy, om, od] = on.split("-").map(Number) as [number, number, number];
  let m = (oy - fy) * 12 + (om - fm);
  if (od < fd) m -= 1;
  return m;
}

/** Letzter Tag der Regel: Austrittstag plus Dauer, minus ein Tag (inklusive). */
export function postTermUntil(from: IsoDate, months: number): IsoDate {
  const [y, m, d] = from.split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  const end = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, last)));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/** Satz der Regel für einen Abschlusstag; außerhalb des Zeitraums null. */
export function postTermBp(rule: PostTermRule, from: IsoDate, on: IsoDate): number | null {
  if (on < from) return null;
  const m = fullMonthsBetween(from, on);
  if (m >= rule.months) return null;
  if (rule.steps?.length) return rule.steps.find((s) => m < s.upto)?.bp ?? null;
  return rule.bp ?? null;
}

/**
 * Satz einer beim Austritt umgestellten laufenden Kooperation für einen Abrechnungsmonat (gleich app.post_term_month_bp):
 * Staffel nach vollen Monaten seit dem Austritt, gezählt ab dem Monatsersten (im Austrittsmonat ab dem Austrittstag);
 * nach dem Ende der Satz „danach“ (after_bp, Standard 0).
 */
export function postTermMonthBp(rule: PostTermRule, from: IsoDate, until: IsoDate | null, month: IsoDate): number {
  const first = `${month.slice(0, 7)}-01`;
  const on = first < from ? from : first;
  const after = rule.after_bp ?? 0;
  if (until && on > until) return after;
  return postTermBp(rule, from, on) ?? after;
}

/** Zeilenfelder der Kooperation zur Nachlaufregel (deals bzw. talent_deals) */
export interface DealPostTermFields {
  commission_bp: number | null;
  post_term?: unknown;
  post_term_from?: string | null;
  post_term_until?: string | null;
}

/**
 * Satz der Kooperation in Basispunkten für einen Abrechnungsmonat: umgestellte laufende Kooperation nach der Staffel,
 * sonst der festgeschriebene Satz. Ohne Monat der festgeschriebene Satz (den der tägliche Lauf auf den laufenden Monat führt).
 */
export function dealBpForMonth(deal: DealPostTermFields, month: string | null | undefined): number | null {
  if (month && /^\d{4}-\d{2}/.test(month) && deal.post_term_from && validPostTermRule(deal.post_term)) {
    return postTermMonthBp(deal.post_term, deal.post_term_from, deal.post_term_until ?? null, `${month.slice(0, 7)}-01`);
  }
  return deal.commission_bp;
}

// ── Satz beim Anlegen ─────────────────────────────────────────────────────────

export type FrozenFrom = "deal" | "post_term" | "origin" | "talent" | "agency";

export interface DealCommissionInput {
  /** an der Kooperation eingetragen (Basispunkte) */
  dealBp?: number | null;
  dealTiers?: unknown;
  origin?: DealOrigin | null;
  originRates?: OriginRates | null;
  talentBp?: number | null;
  talentTiers?: unknown;
  agencyBp: number | null;
  /** Creator archiviert mit Regel: Regel und Zeitraum */
  postTerm?: { rule: PostTermRule; from: IsoDate; until: IsoDate } | null;
  /** Abschlusstag (heute beim Anlegen) */
  on: IsoDate;
}
export interface DealCommission {
  bp: number | null;
  tiers: unknown;
  from: FrozenFrom;
  /** gesetzt, wenn die Nachlaufregel den Satz bestimmt */
  postTermUntil?: IsoDate;
}

export function resolveDealCommission(i: DealCommissionInput): DealCommission {
  const hasTiers = (t: unknown) => Array.isArray(t) && t.length > 0;
  const firstBp = (t: unknown) => Number((t as { bp: number }[])[0]!.bp);
  if (i.dealBp != null) return { bp: i.dealBp, tiers: hasTiers(i.dealTiers) ? i.dealTiers : null, from: "deal" };
  if (hasTiers(i.dealTiers)) return { bp: firstBp(i.dealTiers), tiers: i.dealTiers, from: "deal" };
  const pt = i.postTerm;
  if (pt && (pt.rule.scope ?? "concluded") === "initiated" && i.on >= pt.from && i.on <= pt.until) {
    const bp = postTermBp(pt.rule, pt.from, i.on);
    if (bp !== null) return { bp, tiers: null, from: "post_term", postTermUntil: pt.until };
  }
  const originBp = i.origin ? i.originRates?.[i.origin] : undefined;
  if (originBp != null) return { bp: originBp, tiers: null, from: "origin" };
  if (hasTiers(i.talentTiers)) return { bp: firstBp(i.talentTiers), tiers: i.talentTiers, from: "talent" };
  if (i.talentBp != null) return { bp: i.talentBp, tiers: null, from: "talent" };
  return { bp: i.agencyBp, tiers: null, from: "agency" };
}

/** Kurztext einer Regel für Dialoge und Hinweise, z. B. „12 Monate: 20 % bis Monat 6, danach 10 %“. */
export function postTermText(rule: PostTermRule, pct: (bp: number) => string, lang: "de" | "en" = "de"): string {
  const en = lang === "en";
  const danach = rule.after_bp ? (en ? `; after the end ${pct(rule.after_bp)}` : `; nach Ablauf ${pct(rule.after_bp)}`) : "";
  const dauer = en ? `${rule.months} ${rule.months === 1 ? "month" : "months"}` : `${rule.months} ${rule.months === 1 ? "Monat" : "Monate"}`;
  if (rule.steps?.length) {
    const parts = rule.steps.map((s, k) => (k === 0
      ? (en ? `${pct(s.bp)} up to month ${s.upto}` : `${pct(s.bp)} bis Monat ${s.upto}`)
      : (en ? `then ${pct(s.bp)} up to month ${s.upto}` : `danach ${pct(s.bp)} bis Monat ${s.upto}`)));
    return `${dauer}: ${parts.join(", ")}${danach}`;
  }
  return `${dauer}: ${pct(rule.bp ?? 0)}${danach}`;
}
